import React from "react";
import ReactDOM from "react-dom";
const D = window.SiteData;
/* =========================================================
   珂玺资本 — 用户中心 Dashboard
   通用外壳（左菜单 + 右内容，套官网主题），按 user_type 分流菜单。
   普通用户：概览 / 我的报告 / 个人资料。
   「我的报告」以 submission 为主线：每条提交即一份报告，状态机
   待处理(received)/处理中(processing)/已交付(done,挂 report)/被驳回(rejected)。
   ========================================================= */

const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp3,.wav,.m4a,.aac,.ogg,.flac";

const STATUS = {
  received:   { cn: "待处理", en: "Pending",     color: "var(--mute)" },
  processing: { cn: "处理中", en: "In progress", color: "var(--gold)" },
  done:       { cn: "已交付", en: "Delivered",   color: "var(--gold-bright)" },
  rejected:   { cn: "被驳回", en: "Rejected",    color: "#d66" },
};

function fmtSize(b) { return b > 1e6 ? (b / 1e6).toFixed(1) + " MB" : (b / 1024).toFixed(0) + " KB"; }

function DashboardPage({ lang, setRoute, onLoginClick }) {
  const isZh = lang === "cn";
  const { user, logout } = window.useAuth();

  const [myReports, setMyReports] = React.useState(null);
  const [mySubs, setMySubs] = React.useState(null);

  const [view, setView] = React.useState("overview");
  const [selectedId, setSelectedId] = React.useState(null); // 报告明细：选中的 submission id
  const [creating, setCreating] = React.useState(false);     // 新建报告 modal

  // 个人资料编辑
  const [profile, setProfile] = React.useState({ display_name: "", company: "", email: "", phone: "" });
  const [pwd, setPwd] = React.useState({ current_password: "", new_password: "", confirm_password: "" });
  const [profState, setProfState] = React.useState({ busy: false, ok: null, error: null });
  const [pwdState, setPwdState] = React.useState({ busy: false, ok: null, error: null });

  React.useEffect(() => {
    if (user) setProfile({ display_name: user.display_name || "", company: user.company || "", email: user.email || "", phone: user.phone || "" });
  }, [user && user.id]);

  const loadMine = React.useCallback(() => {
    if (!user) { setMyReports(null); setMySubs(null); return; }
    window.authFetch("/api/studio/reports").then((r) => (r.ok ? r.json() : [])).then(setMyReports).catch(() => {});
    window.authFetch("/api/studio/submissions").then((r) => (r.ok ? r.json() : [])).then(setMySubs).catch(() => {});
  }, [user && user.id]);
  React.useEffect(loadMine, [loadMine]);

  // 保存基本资料（不含密码）
  const saveInfo = async () => {
    setProfState({ busy: true, ok: null, error: null });
    try {
      const body = {};
      if (profile.display_name !== user.display_name) body.display_name = profile.display_name;
      if ((profile.company || "") !== (user.company || "")) body.company = profile.company || null;
      if ((profile.email || "") !== (user.email || "")) body.email = profile.email;
      if ((profile.phone || "") !== (user.phone || "")) body.phone = profile.phone || null;
      if (Object.keys(body).length === 0) { setProfState({ busy: false, ok: null, error: isZh ? "没有可保存的修改" : "Nothing to save" }); return; }
      const res = await window.authFetch("/api/auth/profile", {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "email_taken" ? (isZh ? "该邮箱已被使用" : "Email already in use")
          : (isZh ? "保存失败，请稍后再试" : "Save failed — please retry");
        setProfState({ busy: false, ok: null, error: msg }); return;
      }
      window.updateStoredUser(data);
      setProfState({ busy: false, ok: true, error: null });
    } catch (err) {
      setProfState({ busy: false, ok: null, error: String(err) });
    }
  };

  // 保存密码（独立）
  const savePwd = async () => {
    if (!pwd.new_password) { setPwdState({ busy: false, ok: null, error: isZh ? "请输入新密码" : "Enter new password" }); return; }
    if (pwd.new_password !== pwd.confirm_password) { setPwdState({ busy: false, ok: null, error: isZh ? "两次密码不一致" : "Passwords do not match" }); return; }
    setPwdState({ busy: true, ok: null, error: null });
    try {
      const res = await window.authFetch("/api/auth/profile", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: pwd.current_password, new_password: pwd.new_password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "wrong_password" ? (isZh ? "原密码错误" : "Wrong current password")
          : (isZh ? "保存失败，请稍后再试" : "Save failed — please retry");
        setPwdState({ busy: false, ok: null, error: msg }); return;
      }
      window.updateStoredUser(data);
      setPwd({ current_password: "", new_password: "", confirm_password: "" });
      setPwdState({ busy: false, ok: true, error: null });
    } catch (err) {
      setPwdState({ busy: false, ok: null, error: String(err) });
    }
  };

  // 未登录
  if (!user) {
    return (
      <div className="page" data-screen-label="07 Dashboard">
        <section style={{ paddingTop: "calc(var(--nav-h) + 120px)", paddingBottom: 120 }}>
          <div className="container" style={{ textAlign: "center" }}>
            <div className="eyebrow">{isZh ? "用户中心" : "Dashboard"}</div>
            <h1 className="h-1" style={{ marginTop: 16 }}>{isZh ? "请先登录" : "Please sign in"}</h1>
            <p className="body-lg body-mute" style={{ marginTop: 20, maxWidth: 560, margin: "20px auto 32px" }}>
              {isZh ? "登录后即可新建报告、查看专属报告、管理个人资料。" : "Sign in to create and view your reports."}
            </p>
            <button className="btn" onClick={onLoginClick}>{isZh ? "登录 / 注册" : "Sign in"}<span className="arr"></span></button>
          </div>
        </section>
      </div>
    );
  }

  const menu = [
    { id: "overview", cn: "概览", en: "Overview" },
    { id: "reports", cn: "我的报告", en: "Reports" },
    { id: "profile", cn: "个人资料", en: "Profile" },
  ];
  const cur = menu.find((m) => m.id === view) || (view === "report-detail" ? { cn: "报告明细", en: "Report" } : menu[0]);

  // 报告成果按 submission_id 归并
  const reportBySub = {};
  (myReports || []).forEach((r) => { if (r.submission_id != null) reportBySub[r.submission_id] = r; });
  const selectedSub = (mySubs || []).find((s) => s.id === selectedId) || null;

  return (
    <div className="page" data-screen-label="07 Dashboard">
      <div className="dash-layout" style={{ paddingTop: "var(--nav-h)", display: "grid", gridTemplateColumns: "260px 1fr", minHeight: "calc(100vh - var(--nav-h))" }}>

        {/* 左侧菜单（移动端由 globals.css 的 .dash-* 规则改为顶部横排） */}
        <aside className="dash-aside" style={{ background: "var(--bg-0)", borderRight: "1px solid var(--line)", padding: "40px 0" }}>
          <div style={{ padding: "0 28px 28px" }}>
            <div className="eyebrow">{isZh ? "用户中心" : "Dashboard"}</div>
            <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 20, marginTop: 12 }}>{user.display_name}</div>
            <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 6, letterSpacing: "0.06em", wordBreak: "break-all" }}>{user.email}</div>
            {user.user_type === "lp" && <div className="mono" style={{ color: "var(--gold)", fontSize: 10, marginTop: 8, letterSpacing: "0.1em" }}>LP 投资人</div>}
          </div>
          <nav className="dash-menu">
            {menu.map((m, i) => (
              <a key={m.id} onClick={() => { setView(m.id); setSelectedId(null); }} style={{
                display: "flex", alignItems: "center", gap: 14, padding: "14px 28px", cursor: "pointer",
                borderLeft: `2px solid ${(view === m.id || (view === "report-detail" && m.id === "reports")) ? "var(--gold)" : "transparent"}`,
                background: (view === m.id || (view === "report-detail" && m.id === "reports")) ? "rgba(201,168,107,0.06)" : "transparent",
                color: (view === m.id || (view === "report-detail" && m.id === "reports")) ? "var(--ivory)" : "var(--mute)",
                transition: "all .2s",
              }}>
                <span className="mono" style={{ fontSize: 10, color: (view === m.id || (view === "report-detail" && m.id === "reports")) ? "var(--gold)" : "var(--mute-3)" }}>{String(i + 1).padStart(2, "0")}</span>
                <span style={{ fontSize: 14 }}>{isZh ? m.cn : m.en}</span>
              </a>
            ))}
          </nav>
          <div style={{ padding: "28px", marginTop: 12 }}>
            <a className="text-link" style={{ cursor: "pointer", fontSize: 12 }}
              onClick={() => window.requestLogout(() => { logout(); setRoute("home"); })}>
              {isZh ? "退出登录" : "Sign out"}
            </a>
          </div>
        </aside>

        {/* 右侧内容 */}
        <main className="dash-main" style={{ padding: "40px 48px 80px", maxWidth: 960 }}>
          <div className="eyebrow-mute" style={{ marginBottom: 6 }}>{(isZh ? cur.en : cur.cn).toUpperCase()}</div>
          <h1 className="h-2" style={{ marginBottom: 36 }}>{isZh ? cur.cn : cur.en}</h1>

          {view === "overview" && <Overview isZh={isZh} user={user} mySubs={mySubs} setView={setView} />}

          {view === "reports" && (
            <ReportsList
              isZh={isZh} mySubs={mySubs} reportBySub={reportBySub}
              onNew={() => setCreating(true)}
              onOpen={(id) => { setSelectedId(id); setView("report-detail"); }}
            />
          )}

          {view === "report-detail" && selectedSub && (
            <ReportDetail
              isZh={isZh} sub={selectedSub} report={reportBySub[selectedSub.id]}
              onBack={() => { setView("reports"); setSelectedId(null); }}
              reload={loadMine} fmtSize={fmtSize}
            />
          )}

          {view === "profile" && (
            <ProfileView isZh={isZh} profile={profile} setProfile={setProfile} pwd={pwd} setPwd={setPwd}
              profState={profState} saveInfo={saveInfo} pwdState={pwdState} savePwd={savePwd} />
          )}
        </main>
      </div>

      {creating && (
        <NewReportModal isZh={isZh} fmtSize={fmtSize} onClose={() => setCreating(false)} onDone={() => { setCreating(false); loadMine(); }} />
      )}
    </div>
  );
}

/* ---------- 概览 ---------- */
function StatCell({ label, value }) {
  return (
    <div style={{ background: "var(--bg-1)", padding: "24px 28px" }}>
      <div className="serif-en" style={{ fontSize: 34, color: "var(--gold)", lineHeight: 1 }}>{value}</div>
      <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 10, letterSpacing: "0.08em" }}>{label}</div>
    </div>
  );
}

function Overview({ isZh, user, mySubs, setView }) {
  const acctLabel = user.user_type === "lp" ? (isZh ? "LP 投资人" : "LP") : (isZh ? "普通用户" : "Normal");
  const delivered = (mySubs || []).filter((s) => s.status === "done").length;
  return (
    <>
      <div className="dash-stats" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, background: "var(--line)", border: "1px solid var(--line)", marginBottom: 36 }}>
        <StatCell label={isZh ? "我的报告" : "Reports"} value={mySubs ? mySubs.length : "—"} />
        <StatCell label={isZh ? "已交付" : "Delivered"} value={mySubs ? delivered : "—"} />
        <div style={{ background: "var(--bg-1)", padding: "24px 28px" }}>
          <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 18, color: "var(--gold)", lineHeight: 1.2 }}>{acctLabel}</div>
          <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 10, letterSpacing: "0.08em" }}>{isZh ? "账户类型" : "Account"}</div>
        </div>
      </div>
      <p className="body-mute" style={{ fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
        {isZh ? "欢迎回到珂玺用户中心。在「我的报告」新建报告（上传调研资料），珂玺调研团队制作完成后会交付到此处。"
          : "Welcome back. Create a report under My Reports — our team will deliver it here."}
      </p>
      <a className="text-link" style={{ cursor: "pointer", fontSize: 13 }} onClick={() => setView("reports")}>{isZh ? "我的报告" : "My reports"}</a>
    </>
  );
}

/* ---------- 我的报告：统一列表 / 卡片 ---------- */
function StatusTag({ isZh, status }) {
  const s = STATUS[status] || STATUS.received;
  return <span className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: s.color }}>{isZh ? s.cn : s.en}</span>;
}

function ReportsList({ isZh, mySubs, reportBySub, onNew, onOpen }) {
  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ marginBottom: 24 }}>
        <button className="btn" onClick={onNew}>{isZh ? "新建报告" : "New report"}</button>
      </div>

      {mySubs === null && <div className="body-mute" style={{ fontSize: 13 }}>{isZh ? "加载中…" : "Loading…"}</div>}
      {mySubs && mySubs.length === 0 && (
        <div style={{ background: "var(--bg-1)", border: "1px solid var(--line)", padding: 32, textAlign: "center" }}>
          <p className="body-mute" style={{ fontSize: 13, margin: 0 }}>{isZh ? "暂无报告 —— 点「新建报告」上传调研资料。" : "No reports yet — click New report."}</p>
        </div>
      )}

      {/* 列表：整体边框 + 行分隔 */}
      {mySubs && mySubs.length > 0 && (
        <div style={{ border: "1px solid var(--line)" }}>
          {mySubs.map((s, i) => {
            const rep = reportBySub[s.id];
            return (
              <div key={s.id} onClick={() => onOpen(s.id)} style={{
                padding: "16px 20px", borderBottom: i < mySubs.length - 1 ? "1px solid var(--line)" : "none",
                cursor: "pointer", display: "grid", gridTemplateColumns: "1fr auto auto", gap: 16, alignItems: "center",
              }}>
                <div>
                  <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 15 }}>
                    {s.title || (rep ? (isZh ? rep.title_zh : (rep.title_en || rep.title_zh)) : (isZh ? "未命名" : "Untitled"))}
                  </div>
                  <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 4 }}>{s.files.length} {isZh ? "份附件" : "files"}</div>
                </div>
                <StatusTag isZh={isZh} status={s.status} />
                <span className="mono" style={{ color: "var(--mute-2)", fontSize: 10 }}>{new Date(s.created_at).toLocaleDateString("zh-CN")}</span>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

/* ---------- 报告明细 ---------- */
function FileCards({ files, fmtSize, removable, onRemove }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
      {files.map((f, i) => (
        <div key={i} style={{ border: "1px solid var(--line)", background: "var(--bg-0)", padding: "12px 14px", position: "relative" }}>
          <div style={{ fontSize: 12, color: "var(--ivory-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
          <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 6 }}>{fmtSize(f.size)}</div>
          {removable
            ? <a onClick={() => onRemove(i)} style={{ position: "absolute", top: 8, right: 10, cursor: "pointer", color: "var(--mute)" }}>×</a>
            : (f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" style={{ position: "absolute", top: 8, right: 10, color: "var(--gold)", fontSize: 11 }}>↗</a>)}
        </div>
      ))}
    </div>
  );
}

function ReportDetail({ isZh, sub, report, onBack, reload, fmtSize }) {
  const editable = sub.status === "received" || sub.status === "rejected";
  const [title, setTitle] = React.useState(sub.title || "");
  const [note, setNote] = React.useState(sub.note || "");
  const [keepFiles, setKeepFiles] = React.useState(sub.files || []);
  const [newFiles, setNewFiles] = React.useState([]);
  const [dragging, setDragging] = React.useState(false);
  const [state, setState] = React.useState({ busy: false, error: null, ok: null });
  const fileRef = React.useRef(null);

  const onPick = (e) => { const picked = Array.from(e.target.files); e.target.value = ""; setNewFiles((p) => [...p, ...picked]); };
  const onDrop = (e) => { e.preventDefault(); setDragging(false); setNewFiles((p) => [...p, ...Array.from(e.dataTransfer.files)]); };

  const save = async () => {
    if (!title.trim()) { setState({ busy: false, error: isZh ? "标题为必填" : "Title is required", ok: null }); return; }
    if (!note.trim()) { setState({ busy: false, error: isZh ? "附言为必填" : "Note is required", ok: null }); return; }
    if (keepFiles.length + newFiles.length === 0) { setState({ busy: false, error: isZh ? "至少保留一个附件" : "At least one file", ok: null }); return; }
    setState({ busy: true, error: null, ok: null });
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("note", note.trim());
      fd.append("keep_files", JSON.stringify(keepFiles));
      newFiles.forEach((f) => fd.append("files", f));
      const res = await window.authFetch(`/api/studio/submissions/${sub.id}`, { method: "PATCH", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "unsupported_mime" ? (isZh ? `不支持的文件类型：${data.name || ""}` : `Unsupported: ${data.name || ""}`)
          : data.error === "title_required" ? (isZh ? "标题为必填" : "Title required")
          : data.error === "note_required" ? (isZh ? "附言为必填" : "Note required")
          : (isZh ? "保存失败" : "Save failed");
        setState({ busy: false, error: msg, ok: null }); return;
      }
      setNewFiles([]); setKeepFiles(data.files || []);
      setState({ busy: false, error: null, ok: true });
      reload();
    } catch (err) { setState({ busy: false, error: String(err), ok: null }); }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <a className="text-link" style={{ cursor: "pointer", fontSize: 12, display: "inline-block", marginBottom: 20 }} onClick={onBack}>← {isZh ? "返回列表" : "Back"}</a>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
        <StatusTag isZh={isZh} status={sub.status} />
        <span className="mono" style={{ color: "var(--mute-2)", fontSize: 10 }}>{new Date(sub.created_at).toLocaleString("zh-CN")}</span>
      </div>

      {/* 标题 */}
      <div className="eyebrow-mute" style={{ marginBottom: 10 }}>{isZh ? "标题" : "Title"}</div>
      {editable ? (
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          style={{ width: "100%", background: "var(--bg-0)", border: "1px solid var(--line)", color: "var(--ivory)", padding: "10px 12px", fontSize: 14, marginBottom: 24 }} />
      ) : (
        <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 18, marginBottom: 24 }}>{sub.title || "—"}</div>
      )}

      {/* 被驳回原因 */}
      {sub.status === "rejected" && sub.reject_reason && (
        <div style={{ border: "1px solid #d66", background: "rgba(221,102,102,0.06)", padding: 16, marginBottom: 24 }}>
          <div className="mono" style={{ fontSize: 10, color: "#d66", letterSpacing: "0.08em" }}>{isZh ? "驳回原因" : "REJECTED"}</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>{sub.reject_reason}</div>
        </div>
      )}

      {/* 已交付：报告成果 + 公开/分享 */}
      {sub.status === "done" && report && <DeliveredReport isZh={isZh} report={report} reload={reload} />}

      {/* 往来记录（交付后继续修正）：已交付或处理中均可见 */}
      {(sub.status === "done" || sub.status === "processing") && (
        <MessageThread isZh={isZh} sub={sub} reload={reload} fmtSize={fmtSize} />
      )}

      {/* 附言 */}
      <div className="eyebrow-mute" style={{ marginBottom: 10 }}>{isZh ? "附言" : "Note"}</div>
      {editable ? (
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4}
          style={{ width: "100%", background: "var(--bg-0)", border: "1px solid var(--line)", color: "var(--ivory)", padding: 12, fontSize: 13, resize: "vertical" }} />
      ) : (
        <p className="body-mute" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{sub.note || "—"}</p>
      )}

      {/* 附件 */}
      <div className="eyebrow-mute" style={{ margin: "24px 0 10px" }}>{isZh ? "附件" : "Files"}</div>
      <FileCards files={editable ? keepFiles : sub.files} fmtSize={fmtSize}
        removable={editable} onRemove={(i) => setKeepFiles((p) => p.filter((_, j) => j !== i))} />

      {editable && (
        <>
          {newFiles.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <FileCards files={newFiles} fmtSize={fmtSize} removable onRemove={(i) => setNewFiles((p) => p.filter((_, j) => j !== i))} />
            </div>
          )}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current && fileRef.current.click()}
            style={{
              border: `1px dashed ${dragging ? "var(--gold)" : "var(--line)"}`,
              background: dragging ? "rgba(201,168,107,0.05)" : "transparent",
              padding: "12px 16px", textAlign: "center", cursor: "pointer", marginTop: 12,
            }}>
            <input ref={fileRef} type="file" multiple style={{ display: "none" }} accept={ACCEPT} onChange={onPick} />
            <span style={{ color: "var(--gold)", fontSize: 12 }}>+ {isZh ? "添加附件" : "Add files"}</span>
            <span className="mono" style={{ color: "var(--mute-3)", fontSize: 10, marginLeft: 10 }}>PDF · Office · 音频 · ≤50MB</span>
          </div>

          {state.error && <div style={{ color: "#d66", fontSize: 13, marginTop: 12 }}>{state.error}</div>}
          {state.ok && <div style={{ color: "var(--gold)", fontSize: 13, marginTop: 12 }}>{isZh ? "已保存" : "Saved"}</div>}

          <button className="btn" style={{ marginTop: 16, minWidth: 220, justifyContent: "center" }} disabled={state.busy} onClick={save}>
            {state.busy ? (isZh ? "提交中…" : "Submitting…")
              : sub.status === "rejected" ? (isZh ? "修改并重新提交" : "Resubmit")
              : (isZh ? "保存修改" : "Save")}
          </button>
        </>
      )}
    </div>
  );
}

function DeliveredReport({ isZh, report, reload }) {
  const [copied, setCopied] = React.useState(false);
  const setVis = async (action) => {
    const res = await window.authFetch(`/api/studio/reports/${report.id}/visibility`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action }),
    });
    if (res.ok) reload();
  };
  const reportUrl = report.report_url || report.site_url || report.file_url || "";
  const onCopy = () => { navigator.clipboard?.writeText(reportUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  // 定向报告（托管在珂玺智库站）：点击时全局进度弹窗向智库换一次性链接后跳转（见 shell.jsx ReportOpenModal）。
  // 换设备/换标签页重新点入口即可（旧会话被踢回登录页是安全特性）。
  const directed = !!report.slug && report.visibility !== "public";

  return (
    <div style={{ border: "1px solid var(--line)", background: "var(--bg-1)", padding: 24, marginBottom: 28 }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>{isZh ? "报告成果" : "Report"}</div>
      <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 16 }}>{isZh ? report.title_zh : (report.title_en || report.title_zh)}</div>
      <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 6, letterSpacing: "0.08em" }}>
        {report.report_type.toUpperCase()}{report.pages ? ` · ${report.pages}p` : report.report_count ? ` · ${report.report_count} ${isZh ? "份报告" : "reports"}` : ""}{report.sector ? ` · ${report.sector}` : ""}
        {" · "}<span style={{ color: report.visibility === "public" ? "var(--gold)" : "var(--mute-2)" }}>
          {report.visibility === "public" ? (isZh ? "已公开" : "Public") : report.visibility === "pending" ? (isZh ? "公开审核中" : "Pending") : (isZh ? "私密" : "Private")}
        </span>
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 14, fontSize: 12, flexWrap: "wrap", alignItems: "center" }}>
        {directed ? (
          <a className="text-link" style={{ cursor: "pointer" }} onClick={() => window.openDirectedReport(report.id)}>
            {isZh ? "查看报告" : "View report"} ↗
          </a>
        ) : reportUrl && (
          <a className="text-link" href={reportUrl} target="_blank" rel="noopener noreferrer">
            {isZh ? "查看报告" : "View report"} ↗
          </a>
        )}
        {report.visibility === "private" && <a className="text-link" style={{ cursor: "pointer" }} onClick={() => setVis("request_public")}>{isZh ? "申请公开" : "Request public"}</a>}
        {(report.visibility === "pending" || report.visibility === "public") && <a style={{ cursor: "pointer", color: "var(--mute-2)" }} onClick={() => setVis("make_private")}>{isZh ? "设回私密" : "Make private"}</a>}
        {report.visibility === "public" && (
          <a className="text-link" style={{ cursor: "pointer" }} onClick={onCopy}>{copied ? (isZh ? "已复制" : "Copied") : (isZh ? "复制分享链接" : "Copy share link")}</a>
        )}
      </div>
      {report.visibility !== "public" && (
        <div className="mono" style={{ color: "var(--mute-3)", fontSize: 10, marginTop: 10 }}>{isZh ? "公开后可生成分享链接" : "Share link available once public"}</div>
      )}
    </div>
  );
}

/* ---------- 往来记录（交付后修正对话） ---------- */
function MessageThread({ isZh, sub, reload, fmtSize }) {
  const [msgs, setMsgs] = React.useState(null);
  const [body, setBody] = React.useState("");
  const [files, setFiles] = React.useState([]);
  const [state, setState] = React.useState({ busy: false, error: null });
  const fileRef = React.useRef(null);

  const load = React.useCallback(() => {
    window.authFetch(`/api/studio/submissions/${sub.id}/messages`)
      .then((r) => (r.ok ? r.json() : [])).then(setMsgs).catch(() => setMsgs([]));
  }, [sub.id]);
  React.useEffect(load, [load]);

  const onPick = (e) => { const picked = Array.from(e.target.files); e.target.value = ""; setFiles((p) => [...p, ...picked]); };

  const send = async () => {
    if (!body.trim()) { setState({ busy: false, error: isZh ? "请填写修正说明" : "Message required" }); return; }
    setState({ busy: true, error: null });
    try {
      const fd = new FormData();
      fd.append("body", body.trim());
      files.forEach((f) => fd.append("files", f));
      const res = await window.authFetch(`/api/studio/submissions/${sub.id}/messages`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "unsupported_mime" ? (isZh ? `不支持的文件类型：${data.name || ""}` : `Unsupported: ${data.name || ""}`)
          : data.error === "body_required" ? (isZh ? "请填写修正说明" : "Message required")
          : (isZh ? "发送失败" : "Send failed");
        setState({ busy: false, error: msg }); return;
      }
      setBody(""); setFiles([]); setState({ busy: false, error: null });
      load(); reload();   // reload 主线（done→processing 状态会变）
    } catch (err) { setState({ busy: false, error: String(err) }); }
  };

  return (
    <div style={{ borderTop: "1px solid var(--line)", marginTop: 28, paddingTop: 24, marginBottom: 8 }}>
      <div className="eyebrow-mute" style={{ marginBottom: 16 }}>{isZh ? "修正往来" : "Revisions"}</div>

      {msgs === null && <div className="body-mute" style={{ fontSize: 12 }}>{isZh ? "加载中…" : "Loading…"}</div>}
      {msgs && msgs.length === 0 && (
        <p className="body-mute" style={{ fontSize: 12, marginBottom: 16 }}>
          {isZh ? "如需对报告进行修正，可在下方提交说明与补充材料。" : "Submit a note and materials below to request revisions."}
        </p>
      )}
      {msgs && msgs.map((m) => {
        const mine = m.author_role === "user";
        return (
          <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 14 }}>
            <div style={{ maxWidth: "78%", background: mine ? "rgba(201,168,107,0.08)" : "var(--bg-1)", border: "1px solid var(--line)", padding: "12px 14px" }}>
              <div className="mono" style={{ fontSize: 9, letterSpacing: "0.08em", color: mine ? "var(--gold)" : "var(--mute-2)", marginBottom: 6 }}>
                {mine ? (isZh ? "我" : "Me") : (isZh ? "珂玺团队" : "Kexi")} · {new Date(m.created_at).toLocaleString("zh-CN")}
              </div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: "var(--ivory-2)" }}>{m.body}</div>
              {m.files && m.files.length > 0 && (
                <div style={{ marginTop: 10 }}><FileCards files={m.files} fmtSize={fmtSize} /></div>
              )}
            </div>
          </div>
        );
      })}

      {/* 发起修正 */}
      <div style={{ marginTop: 16 }}>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
          placeholder={isZh ? "说明希望修正的内容…" : "Describe the changes you'd like…"}
          style={{ width: "100%", background: "var(--bg-0)", border: "1px solid var(--line)", color: "var(--ivory)", padding: 12, fontSize: 13, resize: "vertical" }} />
        {files.length > 0 && <div style={{ marginTop: 10 }}><FileCards files={files} fmtSize={fmtSize} removable onRemove={(i) => setFiles((p) => p.filter((_, j) => j !== i))} /></div>}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
          <a className="text-link" style={{ cursor: "pointer", fontSize: 12 }} onClick={() => fileRef.current && fileRef.current.click()}>+ {isZh ? "补充材料" : "Add files"}</a>
          <input ref={fileRef} type="file" multiple style={{ display: "none" }} accept={ACCEPT} onChange={onPick} />
          <span className="mono" style={{ color: "var(--mute-3)", fontSize: 10 }}>PDF · Office · 音频 · ≤50MB</span>
        </div>
        {state.error && <div style={{ color: "#d66", fontSize: 13, marginTop: 10 }}>{state.error}</div>}
        <button className="btn" style={{ marginTop: 14, minWidth: 200, justifyContent: "center" }} disabled={state.busy} onClick={send}>
          {state.busy ? (isZh ? "发送中…" : "Sending…") : (isZh ? "提交修正" : "Submit revision")}
        </button>
      </div>
    </div>
  );
}

/* ---------- 新建报告 Modal ---------- */
function NewReportModal({ isZh, fmtSize, onClose, onDone }) {
  const [files, setFiles] = React.useState([]);
  const [title, setTitle] = React.useState("");
  const [note, setNote] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const [state, setState] = React.useState({ busy: false, error: null });
  const fileRef = React.useRef(null);
  window.useScrollLock(true);  // 弹窗挂载即锁定背景滚动
  const submitRef = React.useRef(null);
  window.useModalKeys(true, { onClose, onSubmit: () => submitRef.current && submitRef.current() });

  const onPick = (e) => { const picked = Array.from(e.target.files); e.target.value = ""; setFiles((p) => [...p, ...picked]); };
  const onDrop = (e) => { e.preventDefault(); setDragging(false); setFiles((p) => [...p, ...Array.from(e.dataTransfer.files)]); };

  const submit = async () => {
    if (!title.trim()) { setState({ busy: false, error: isZh ? "标题为必填" : "Title is required" }); return; }
    if (!note.trim()) { setState({ busy: false, error: isZh ? "附言为必填" : "Note is required" }); return; }
    if (files.length === 0) { setState({ busy: false, error: isZh ? "请上传至少一个附件" : "Upload at least one file" }); return; }
    setState({ busy: true, error: null });
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("title", title.trim());
      fd.append("note", note.trim());
      const res = await window.authFetch("/api/studio/submissions", { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "unsupported_mime" ? (isZh ? `不支持的文件类型：${data.name || ""}` : `Unsupported: ${data.name || ""}`)
          : data.error === "file_too_large" ? (isZh ? "单个文件不能超过 50 MB" : "File too large")
          : data.error === "title_required" ? (isZh ? "标题为必填" : "Title required")
          : data.error === "note_required" ? (isZh ? "附言为必填" : "Note required")
          : (isZh ? "提交失败，请稍后再试" : "Submit failed");
        setState({ busy: false, error: msg }); return;
      }
      onDone();
    } catch (err) { setState({ busy: false, error: String(err) }); }
  };
  submitRef.current = submit;

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <button className="close" onClick={onClose}>×</button>
        <div className="eyebrow">{isZh ? "新建报告" : "New report"}</div>
        <h2 className="h-3" style={{ marginTop: 12, marginBottom: 8 }}>{isZh ? "上传调研资料" : "Upload materials"}</h2>
        <p className="body-mute" style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 20 }}>
          {isZh ? "填写标题、上传资料并填写附言，珂玺调研团队将据此制作报告并交付到「我的报告」。" : "Add a title, upload materials with a note — we will produce and deliver the report."}
        </p>

        <div className="eyebrow-mute" style={{ marginBottom: 8 }}>{isZh ? "标题（必填）" : "Title (required)"}</div>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder={isZh ? "报告标题" : "Report title"}
          style={{ width: "100%", background: "var(--bg-0)", border: "1px solid var(--line)", color: "var(--ivory)", padding: "10px 12px", fontSize: 13, marginBottom: 16 }} />

        {/* 缩小的拖拽区 */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{
            border: `1px dashed ${dragging ? "var(--gold)" : "var(--line)"}`,
            background: dragging ? "rgba(201,168,107,0.05)" : "var(--bg-0)",
            padding: "14px 16px", textAlign: "center", cursor: "pointer",
          }}>
          <input ref={fileRef} type="file" multiple style={{ display: "none" }} accept={ACCEPT} onChange={onPick} />
          <span style={{ color: "var(--gold)", fontSize: 13 }}>{isZh ? "拖入文件或点击选择" : "Drop or click to upload"}</span>
          <span className="mono" style={{ color: "var(--mute-3)", fontSize: 10, marginLeft: 10 }}>
            {isZh ? "支持 PDF / DOCX / PPTX / MP3 · 单文件 ≤ 50MB" : "PDF / DOCX / PPTX / MP3 · ≤ 50MB each"}
          </span>
        </div>

        {/* 格式标签 */}
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {["PDF", "DOCX", "PPTX", "XLSX", "MP3", "Markdown"].map((fmt) => (
            <span key={fmt} style={{ fontFamily: "var(--f-mono)", fontSize: 11, padding: "5px 11px", border: "1px solid var(--line)", color: "var(--mute)", letterSpacing: "0.1em" }}>{fmt}</span>
          ))}
        </div>

        {files.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <FileCards files={files} fmtSize={fmtSize} removable onRemove={(i) => setFiles((p) => p.filter((_, j) => j !== i))} />
          </div>
        )}

        <div className="eyebrow-mute" style={{ margin: "20px 0 8px" }}>{isZh ? "附言（必填）" : "Note (required)"}</div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4}
          placeholder={isZh ? "请说明希望我们关注的方向、背景…" : "Describe the focus and context…"}
          style={{ width: "100%", background: "var(--bg-0)", border: "1px solid var(--line)", color: "var(--ivory)", padding: 12, fontSize: 13, resize: "vertical" }} />

        {state.error && <div style={{ color: "#d66", fontSize: 13, marginTop: 12 }}>{state.error}</div>}

        <button className="btn" style={{ marginTop: 16, width: "100%", justifyContent: "center" }} disabled={state.busy} onClick={submit}>
          {state.busy ? (isZh ? "提交中…" : "Submitting…") : (isZh ? "提交" : "Submit")}
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ---------- 个人资料 ---------- */
function ProfileView({ isZh, profile, setProfile, pwd, setPwd, profState, saveInfo, pwdState, savePwd }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "start", maxWidth: 900 }}>
      {/* 左：基本资料 */}
      <div>
        <div className="form-field">
          <label>{isZh ? "姓名" : "Name"}</label>
          <input type="text" value={profile.display_name} onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>{isZh ? "邮箱（登录账号）" : "Email (login)"}</label>
          <input type="email" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>{isZh ? "电话" : "Phone"}</label>
          <input type="text" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>{isZh ? "机构 / 公司" : "Company"}</label>
          <input type="text" value={profile.company} onChange={(e) => setProfile((p) => ({ ...p, company: e.target.value }))} />
        </div>

        {profState.error && <div style={{ color: "#d66", fontSize: 13, marginTop: 4 }}>{profState.error}</div>}
        {profState.ok && <div style={{ color: "var(--gold)", fontSize: 13, marginTop: 4 }}>{isZh ? "已保存" : "Saved"}</div>}

        <button className="btn" style={{ marginTop: 20, minWidth: 180, justifyContent: "center" }} disabled={profState.busy} onClick={saveInfo}>
          {profState.busy ? (isZh ? "保存中…" : "Saving…") : (isZh ? "保存资料" : "Save")}
        </button>
      </div>

      {/* 右：密码（线框圈起，独立保存） */}
      <div style={{ border: "1px solid var(--line)", padding: 28 }}>
        <div className="eyebrow" style={{ marginBottom: 20 }}>{isZh ? "修改密码" : "Change password"}</div>
        <div className="form-field">
          <label>{isZh ? "原密码" : "Current password"}</label>
          <input type="password" value={pwd.current_password} autoComplete="current-password" onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>{isZh ? "新密码（至少 6 位）" : "New password (min 6)"}</label>
          <input type="password" value={pwd.new_password} autoComplete="new-password" onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>{isZh ? "确认新密码" : "Confirm new password"}</label>
          <input type="password" value={pwd.confirm_password} autoComplete="new-password" onChange={(e) => setPwd((p) => ({ ...p, confirm_password: e.target.value }))} />
        </div>

        {pwdState.error && <div style={{ color: "#d66", fontSize: 13, marginTop: 4 }}>{pwdState.error}</div>}
        {pwdState.ok && <div style={{ color: "var(--gold)", fontSize: 13, marginTop: 4 }}>{isZh ? "密码已更新" : "Password updated"}</div>}

        <button className="btn" style={{ marginTop: 20, minWidth: 180, justifyContent: "center" }} disabled={pwdState.busy} onClick={savePwd}>
          {pwdState.busy ? (isZh ? "保存中…" : "Saving…") : (isZh ? "保存密码" : "Save password")}
        </button>
      </div>
    </div>
  );
}

Object.assign(window, { DashboardPage });
